import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdGateInfoOpenBehavior } from '../../chart/bodygraph/gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../../chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { IChingLinePosition } from '../core/iching.models';
import { HdIChingAccessService } from '../hd-iching-access.service';
import { HdIChingPanelComponent } from './hd-iching-panel.component';
import {
  HD_ICHING_PANEL_KEY, HdIChingBirthTarget, HdIChingDayTarget, HdIChingPairTarget, HdIChingPanelData, HdIChingPanelTarget,
} from './hd-iching-panel.models';

/**
 * Opent het Classic I Ching-paneel. Zelfde patroon als
 * `HdLineCompanionPanelService`: één paneel (key `classic-iching`), dock-/
 * dialoogkeuze uit de globale `gateInfoOpenBehavior`-setting die chart-pagina's
 * live doorgeven, en `replaceData` met een verse referentie zodat een al open
 * paneel opnieuw navigeert/scrollt (ook naar dezelfde lijn). Op pagina's zonder
 * dock-zone floatt het paneel als dialoog.
 *
 * Alle opens zijn no-ops zonder toegang (feature + setting, via
 * `classicVisible()`), zodat een vergeten guard bij een entry-point nooit
 * content lekt. Valt de toegang weg terwijl het paneel open is (setting uit,
 * feature ingetrokken), dan sluit het paneel.
 */
@Injectable({ providedIn: 'root' })
export class HdIChingPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly access = inject(HdIChingAccessService);

  private readonly openBehavior = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  constructor() {
    // Alleen op de overgang zichtbaar → onzichtbaar: bij de opstart is de
    // feature nog niet geladen (false) en mag er niets gesloten worden.
    let wasVisible = false;
    effect(() => {
      const visible = this.access.classicVisible();
      if (wasVisible && !visible) {
        untracked(() => this.floatingPanels.closeForKey(HD_ICHING_PANEL_KEY));
      }
      wasVisible = visible;
    });
  }

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehavior.set(behavior);
  }

  /** Opent het paneel op het hexagrammenoverzicht. */
  openOverview(origin?: { x: number; y: number }): void {
    this.open({ view: 'overview' }, origin);
  }

  /**
   * Opent het paneel op een hexagram; met `focusLine` scrollt het paneel
   * automatisch naar de betreffende lijn.
   */
  openHexagram(
    number: number,
    options?: { focusLine?: IChingLinePosition; origin?: { x: number; y: number } },
  ): void {
    const target: HdIChingPanelTarget =
      options?.focusLine != null
        ? { view: 'hexagram', number, focusLine: options.focusLine }
        : { view: 'hexagram', number };
    this.open(target, options?.origin);
  }

  /** Hexagram van de dag (zon, maan, Chinese dagteller) voor een dag of moment. */
  openDay(target: HdIChingDayTarget, origin?: { x: number; y: number }): void {
    this.open(target, origin);
  }

  /** Jouw hexagram: bewuste en onbewuste zon, Plum Blossom-triade. */
  openBirth(target: HdIChingBirthTarget, origin?: { x: number; y: number }): void {
    this.open(target, origin);
  }

  /** Twee hexagrammen (composite): bewuste zon van beide personen. */
  openPair(target: HdIChingPairTarget, origin?: { x: number; y: number }): void {
    this.open(target, origin);
  }

  private open(target: HdIChingPanelTarget, origin?: { x: number; y: number }): void {
    if (!this.access.classicVisible()) {
      return;
    }
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
    const data: HdIChingPanelData = { target };

    const ref = this.floatingPanels.openOrFocus<HdIChingPanelComponent, HdIChingPanelData>({
      key: HD_ICHING_PANEL_KEY,
      component: HdIChingPanelComponent,
      data,
      dockTab: { label: 'IC', icon: 'scroll-old' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null ? { preferred: origin, hint: 'prefer-right-of-anchor' } : 'center',
    });
    // Verse referentie: ook een al open paneel navigeert/scrollt opnieuw.
    ref.replaceData({ ...data });
  }
}
